import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { BreadcrumbItemType } from 'types/breadcrumb';
import { CartItemType } from 'types/cart';
import { CurrentCustomerType } from 'types/customer';
import {
    GtmCartInfoType,
    GtmChangeCartItemEventType,
    GtmEcommerceEventType,
    GtmEventType,
    GtmListNameType,
    GtmMessageEventType,
    GtmMessageOriginType,
    GtmMessageType,
    GtmPageInfoType,
    GtmPageType,
    GtmPageViewEventType,
    GtmPaymentInfoEventType,
    GtmProductDetailEventType,
    GtmProductsListEventType,
    GtmSearchEventType,
    GtmSectionType,
    GtmShippingInfoEventType,
    GtmUserEntryEventType,
} from 'types/gtm';
import { PaymentType } from 'types/payment';
import { PickupPointType } from 'types/pickupPoint';
import {
    AutocompleteProductType,
    ListedProductType,
    ProductDetailType,
    SimpleProductType,
    VariantDetailType,
} from 'types/product';
import { AutocompleteSearchType } from 'types/search';
import { MappedTransportType } from 'types/transport';
import { getGtmConsentInfo, getGtmPageInfoType, getGtmUserInfo, useGtmCartEventInfo } from 'utils/Gtm/Gtm';
import { getGtmDeviceType } from 'utils/Gtm/Helpers';
import {
    mapGtmCartItemType,
    mapGtmListedProductType,
    mapGtmProductDetailType,
    mapGtmShippingInfo,
    mapGtmUserEntryInfoFromCurrentCustomer,
} from 'utils/Gtm/Mappers';

export const useGtmStaticPageViewEvent = (
    pageType: GtmPageType,
    breadcrumbs?: BreadcrumbItemType[],
): GtmPageViewEventType => {
    const path = useRouter().asPath;
    const gtmPageInfo = useMemo(() => getGtmPageInfoType(pageType, path, breadcrumbs), [pageType, path, breadcrumbs]);

    return useGtmPageViewEvent(gtmPageInfo);
};

export const useGtmPageViewEvent = (pageInfo: GtmPageInfoType): GtmPageViewEventType => {
    const domainConfig = useDomainConfig();
    const { user } = useCurrentUserData();
    const cartEventInfo = useGtmCartEventInfo();

    return useMemo(
        () => ({
            event: 'page_ready',
            page: pageInfo,
            user: getGtmUserInfo(user),
            device: getGtmDeviceType(),
            consent: getGtmConsentInfo(),
            currency: domainConfig.currencyCode,
            language: domainConfig.defaultLocale,
            cart: cartEventInfo.cart,
            _clear: true,
            _isLoaded: cartEventInfo.isLoaded,
        }),
        [
            cartEventInfo.cart,
            cartEventInfo.isLoaded,
            domainConfig.currencyCode,
            domainConfig.defaultLocale,
            pageInfo,
            user,
        ],
    );
};

export const getNewGtmEcommerceEvent = (eventType: GtmEventType, clear = false): GtmEcommerceEventType => ({
    event: eventType,
    ecommerce: undefined,
    _clear: clear,
});

export const getGtmChangeCartItemEvent = (
    cartItem: CartItemType,
    listIndex: number | null,
    quantity: number,
    currencyCode: string,
    eventValue: number,
    eventValueWithTax: number,
    listName: GtmListNameType,
    domainUrl: string,
): GtmChangeCartItemEventType => ({
    listName,
    value: eventValue,
    valueWithTax: eventValueWithTax,
    currency: currencyCode,
    products: [mapGtmCartItemType(cartItem, listIndex, domainUrl, quantity)],
});

export const getGtmChangeCartItemsEvent = (
    products: GtmChangeCartItemEventType['products'],
    currencyCode: string,
    eventValue: number,
    eventValueWithTax: number,
    listName: GtmListNameType,
): GtmChangeCartItemEventType => ({
    listName,
    value: eventValue,
    valueWithTax: eventValueWithTax,
    currency: currencyCode,
    products,
});

export const getGtmShippingInfoEvent = (
    cartInfoType: GtmCartInfoType,
    transport: MappedTransportType,
    pickupPoint: PickupPointType | null,
    paymentName: string | undefined,
): GtmShippingInfoEventType => {
    const { shippingDetail } = mapGtmShippingInfo(pickupPoint);

    return {
        value: cartInfoType.value,
        valueWithTax: cartInfoType.valueWithTax,
        coupons: cartInfoType.coupons,
        products: cartInfoType.products,
        currency: cartInfoType.currency,
        paymentType: paymentName,
        shippingType: transport.name,
        shippingDetail: shippingDetail,
        shippingPrice: transport.price.priceWithoutVat,
        shippingPriceWithTax: transport.price.priceWithVat,
    };
};

export const getGtmPaymentInfoEvent = (
    cartInfoType: GtmCartInfoType,
    payment: PaymentType,
): GtmPaymentInfoEventType => ({
    value: cartInfoType.value,
    valueWithTax: cartInfoType.valueWithTax,
    coupons: cartInfoType.coupons,
    products: cartInfoType.products,
    currency: cartInfoType.currency,
    paymentType: payment.name,
    paymentPrice: payment.price.priceWithoutVat,
    paymentPriceWithTax: payment.price.priceWithVat,
});

export const getGtmProductsListEvent = (
    products: ListedProductType[],
    listName: GtmListNameType,
    currentPage: number,
    pageSize: number,
    domainUrl: string,
): GtmProductsListEventType => {
    return {
        listName,
        products: products.map((product: ListedProductType, index) => {
            const listedProductIndex = (currentPage - 1) * pageSize + index;

            return mapGtmListedProductType(product, listedProductIndex, domainUrl);
        }),
    };
};

export const getGtmProductDetailOnClickEvent = (
    product: ListedProductType | SimpleProductType | AutocompleteProductType,
    listName: GtmListNameType,
    index: number,
    domainUrl: string,
): GtmProductsListEventType => ({
    listName,
    products: [mapGtmListedProductType(product, index, domainUrl)],
});

export const getGtmProductDetailEvent = (
    product: ProductDetailType | VariantDetailType,
    currencyCode: string,
    domainUrl: string,
): GtmProductDetailEventType => ({
    currency: currencyCode,
    value: product.price.priceWithoutVat,
    valueWithTax: product.price.priceWithVat,
    products: [mapGtmProductDetailType(product, 0, domainUrl)],
});

export const getGtmSearchResultEvent = (searchResult: AutocompleteSearchType, keyword: string): GtmSearchEventType => {
    const resultsCount =
        searchResult.categoriesSearch.totalCount +
        searchResult.productsSearch.totalCount +
        searchResult.articlesSearch.length +
        searchResult.brandSearch.length;
    const suggestResult = {
        results: resultsCount,
        keyword,
        sections: {
            categories: searchResult.categoriesSearch.totalCount,
            products: searchResult.productsSearch.totalCount,
            articles: searchResult.articlesSearch.length,
            manufacturers: searchResult.brandSearch.length,
        },
    };

    return {
        event: 'ec.suggest_result',
        suggestResult,
    };
};

export const getGtmSearchClickEvent = (
    keyword: string,
    section: GtmSectionType,
    itemName: string,
): GtmSearchEventType => ({
    event: 'ec.suggest_click',
    suggestClick: {
        keyword,
        itemName,
        section,
    },
});

export const getGtmMessageEvent = (
    type: GtmMessageType,
    message: string,
    detail: string,
    origin?: GtmMessageOriginType,
): GtmMessageEventType => ({
    event: 'ec.message',
    eventParameters: {
        type,
        origin,
        detail,
        message,
    },
});

export const getGtmUserEntryEvent = (
    type: 'ec.login' | 'ec.registration',
    user: CurrentCustomerType,
): GtmUserEntryEventType => ({
    event: type,
    user: mapGtmUserEntryInfoFromCurrentCustomer(user),
    _clear: true,
});
